import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { StripeElementsService } from '../../services/stripe/stripe-elements.service';
import { StripePaymentMode } from './models/stripe-payment-mode.enum';
import { StripeElementValidationState } from './models/stripe-validation-state.interface';
import { DEFAULT_ELEMENT_READY_DELAY } from '../../services/stripe/models/stripe-config.interface';
import { StripeCardNumberElementChangeEvent, StripeCardExpiryElementChangeEvent, StripeCardCvcElementChangeEvent, StripeAddressElementChangeEvent } from '@stripe/stripe-js';

@Component({
  selector: 'app-stripe-elements',
  templateUrl: './stripe-elements.component.html',
  styleUrls: ['./stripe-elements.component.scss']
})
export class StripeElementsComponent implements AfterViewInit, OnDestroy {
  @ViewChild('cardNumberContainer') private cardNumberContainerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('cardExpiryContainer') private cardExpiryContainerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('cardCvcContainer') private cardCvcContainerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('addressContainer') private addressContainerRef!: ElementRef<HTMLDivElement>;

  @Input() publishableKey!: string;
  @Input() mode: StripePaymentMode = StripePaymentMode.Token;

  @Output() ready = new EventEmitter<void>();
  @Output() validationChange = new EventEmitter<boolean>();

  validationState: StripeElementValidationState = {
    isCardNumberValid: false,
    isCardExpiryValid: false,
    isCardCvcValid: false,
    isAddressValid: false
  };

  areElementsLoaded = false;
  cardFieldFocused = false;
  cardFieldError: string | null = null;
  errorMessage: string | null = null;

  constructor(private stripeElementsService: StripeElementsService) {}

  async ngAfterViewInit(): Promise<void> {
    await this.initializeStripeElements();
  }

  ngOnDestroy(): void {
    this.stripeElementsService.destroy();
  }

  private async initializeStripeElements(): Promise<void> {
    try {
      this.validatePublishableKey();
      await this.stripeElementsService.initialize(this.publishableKey);
      this.stripeElementsService.createElements();
      this.mountAllElements();
      this.setupElementEventListeners();
      this.waitForElementsReady();
    } catch (error: any) {
      this.handleInitializationError(error);
    }
  }

  private validatePublishableKey(): void {
    if (!this.publishableKey) {
      throw new Error('Stripe publishable key is required');
    }
  }

  private mountAllElements(): void {
    const {
      cardNumberElement,
      cardExpiryElement,
      cardCvcElement,
      addressElement
    } = this.stripeElementsService;

    if (cardNumberElement) {
      this.stripeElementsService.mountCardNumber(this.cardNumberContainerRef.nativeElement);
    }

    if (cardExpiryElement) {
      this.stripeElementsService.mountCardExpiry(this.cardExpiryContainerRef.nativeElement);
    }

    if (cardCvcElement) {
      this.stripeElementsService.mountCardCvc(this.cardCvcContainerRef.nativeElement);
    }

    if (addressElement) {
      this.stripeElementsService.mountAddress(this.addressContainerRef.nativeElement);
    }
  }

  private setupElementEventListeners(): void {
    this.setupCardFocusListeners();
    this.setupCardNumberListener();
    this.setupCardExpiryListener();
    this.setupCardCvcListener();
    this.setupAddressListener();
  }

  private setupCardFocusListeners(): void {
    const { cardNumberElement, cardExpiryElement, cardCvcElement } = this.stripeElementsService;
    const onFocus = () => (this.cardFieldFocused = true);
    const onBlur = () => (this.cardFieldFocused = false);

    cardNumberElement?.on('focus', onFocus);
    cardNumberElement?.on('blur', onBlur);
    cardExpiryElement?.on('focus', onFocus);
    cardExpiryElement?.on('blur', onBlur);
    cardCvcElement?.on('focus', onFocus);
    cardCvcElement?.on('blur', onBlur);
  }

  private setupCardNumberListener(): void {
    this.stripeElementsService.cardNumberElement?.on('change', (event: StripeCardNumberElementChangeEvent) => {
      this.handleCardElementChange(event, 'isCardNumberValid');
    });
  }

  private setupCardExpiryListener(): void {
    this.stripeElementsService.cardExpiryElement?.on('change', (event: StripeCardExpiryElementChangeEvent) => {
      this.handleCardElementChange(event, 'isCardExpiryValid');
    });
  }

  private setupCardCvcListener(): void {
    this.stripeElementsService.cardCvcElement?.on('change', (event: StripeCardCvcElementChangeEvent) => {
      this.handleCardElementChange(event, 'isCardCvcValid');
    });
  }

  private setupAddressListener(): void {
    this.stripeElementsService.addressElement?.on('change', (event: StripeAddressElementChangeEvent) => {
      this.validationState.isAddressValid = event.complete;
      this.emitValidationState();
    });
  }

  private handleCardElementChange(
    event: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
    validationKey: keyof StripeElementValidationState
  ): void {
    this.validationState[validationKey] = event.complete;

    this.cardFieldError = event.error?.message ?? null;
    this.errorMessage = null;

    this.emitValidationState();
  }

  private emitValidationState(): void {
    const isFormValid = this.isValid();
    this.validationChange.emit(isFormValid);
  }

  private waitForElementsReady(): void {
    this.stripeElementsService.addressElement?.on('ready', () => {
      setTimeout(() => {
        this.areElementsLoaded = true;
        this.stripeElementsService.cardNumberElement?.focus();
        this.ready.emit();
      }, DEFAULT_ELEMENT_READY_DELAY);
    });
  }

  private handleInitializationError(error: Error): void {
    console.error('Stripe initialization error:', error);
    this.errorMessage = error.message || 'Payment form failed to load';
  }

  isValid(): boolean {
    return (
      this.validationState.isCardNumberValid &&
      this.validationState.isCardExpiryValid &&
      this.validationState.isCardCvcValid &&
      this.validationState.isAddressValid
    );
  }

  async submit(): Promise<string | any> {
    if (!this.isValid()) {
      throw new Error('Please complete all required fields');
    }

    try {
      if (this.mode === StripePaymentMode.Token) {
        return await this.stripeElementsService.createToken();
      } else {
        return await this.stripeElementsService.createPaymentMethod();
      }
    } catch (error: any) {
      this.errorMessage = error.message || 'Failed to process payment information';
      throw error;
    }
  }
}
